import React, { useEffect, useState } from 'react'
import PropTypes from 'prop-types'
import {
  StyledGamesListWrapper,
  StyledGameTileWrapper,
  StyledListAndButtonWrapper,
} from '../gamesList/styles'
import { ClickableGameTile } from '../../atoms/clickableGameTile'
import { GameTileText } from '../../atoms/gameTileText'
import { InfiniteScroll } from '../../atoms/InfiniteScroll'
import useSlugs from '../../../hooks/useSlugs'
import { Pagination } from '../../atoms/Pagination'
import { useLocation } from '@reach/router'
import { If } from '../../atoms/if'
import { Button } from '../../atoms/button'
import { useTranslation } from '../../../context/translationProvider'
import { usePagination } from '../../../hooks/usePagination'
import { INFINITE_SCROLL_PAGE_SIZE } from '../../../utils/constants'
import { parseQueryString } from '../../../utils/generic'

const DefaultGameGrid = ({ games, rows, columns, createGameTile }) => {
  const { translate } = useTranslation()
  const [page, setPage] = useState(1)
  const onShowMore = () => setPage((current) => current + 1)

  const maxLength = rows * columns * page
  const gameList = games.slice(0, maxLength)
  const renderButton = gameList.length < games.length

  return (
    <StyledListAndButtonWrapper>
      <StyledGamesListWrapper $columns={columns}>
        {gameList.map((game, index) => createGameTile(game, index))}
      </StyledGamesListWrapper>
      <If
        condition={renderButton}
        render={() => (
          <Button bordered onClick={onShowMore}>
            {translate('common.showMore')}
          </Button>
        )}
      />
    </StyledListAndButtonWrapper>
  )
}

const InfiniteScrollGameGrid = ({ games, reset, createGameTile, columns }) => {
  const { pathname } = useLocation()

  const {
    list,
    isLastPage,
    selectPage,
    nextPage,
    page,
    setPage,
    setInitialPage,
  } = usePagination({
    items: games,
    pathname,
    dependencies: [reset],
  })

  const getInitialPage = () => {
    const { page: queryPage } = parseQueryString(window?.location?.search || '')
    const defaultPage = queryPage ? Number(queryPage) : 1
    if (defaultPage > games.length / INFINITE_SCROLL_PAGE_SIZE) {
      return Math.floor(games.length / INFINITE_SCROLL_PAGE_SIZE)
    }

    return defaultPage - 1
  }

  useEffect(() => {
    const defaultPage = getInitialPage()
    setInitialPage(defaultPage)
    setPage(defaultPage)
  }, [])

  return (
    <InfiniteScroll
      currentPage={page}
      loadNextPage={nextPage}
      isLastPage={isLastPage}
    >
      <StyledListAndButtonWrapper>
        <StyledGamesListWrapper $columns={columns}>
          {list.map((game, index) => createGameTile(game, index))}
        </StyledGamesListWrapper>
      </StyledListAndButtonWrapper>
      <Pagination
        onPageChange={selectPage}
        pageRangeDisplayed={5}
        pageCount={games.length / INFINITE_SCROLL_PAGE_SIZE}
        initialPage={page}
        href={pathname}
      />
    </InfiniteScroll>
  )
}

export const GameGrid = ({
  rows,
  columns,
  games,
  reset,
  hideText,
  isWide,
  forceLogin,
  enableInfiniteScrolling = false,
  screenName,
  triggerGAEvent,
  categoryName,
}) => {
  const [baseProvidersSlug, casinoSlug, loginSlug] = useSlugs([
    'providers',
    'casino',
    'login',
  ])

  const createGameTileWithText = (slide, position) => (
    <div key={`search-game-tile-${slide.slug}`}>
      <StyledGameTileWrapper>
        <ClickableGameTile
          game={slide}
          isWide={isWide}
          casinoSlug={casinoSlug}
          loginSlug={loginSlug}
          forceLogin={forceLogin}
          triggerGAEvent={triggerGAEvent}
          screenName={screenName}
          position={position}
          category={categoryName}
        />
      </StyledGameTileWrapper>
      <GameTileText
        title={slide.title}
        providerName={slide.providerName}
        gameSlug={slide.providerSlug || slide.providerCode}
        baseProvidersSlug={baseProvidersSlug}
      />
    </div>
  )

  const createGameTileNoText = (slide, position) => (
    <div key={`search-game-tile-${slide.slug}`}>
      <StyledGameTileWrapper>
        <ClickableGameTile
          game={slide}
          isWide={isWide}
          casinoSlug={casinoSlug}
          loginSlug={loginSlug}
          forceLogin={forceLogin}
          triggerGAEvent={triggerGAEvent}
          screenName={screenName}
          position={position}
          category={categoryName}
        />
      </StyledGameTileWrapper>
    </div>
  )

  //? This way may seem a little messier, but it reduces the number of if statements
  const createGameTile = hideText
    ? createGameTileNoText
    : createGameTileWithText

  if (enableInfiniteScrolling) {
    return (
      <InfiniteScrollGameGrid
        createGameTile={createGameTile}
        games={games}
        reset={reset}
        columns={columns}
      />
    )
  }

  return (
    <DefaultGameGrid
      rows={rows}
      columns={columns}
      games={games}
      createGameTile={createGameTile}
    />
  )
}

GameGrid.defaultProps = {
  columns: 0,
  games: 0,
  reset: false,
  hideText: false,
  isWide: false,
  forceLogin: false,
  enableInfiniteScrolling: false,
  rows: 0,
}

GameGrid.propTypes = {
  columns: PropTypes.number,
  games: PropTypes.number,
  reset: PropTypes.bool,
  hideText: PropTypes.bool,
  isWide: PropTypes.bool,
  forceLogin: PropTypes.bool,
  enableInfiniteScrolling: PropTypes.bool,
  rows: PropTypes.number,
  screenName: PropTypes.string,
  triggerGAEvent: PropTypes.bool,
  categoryName: PropTypes.string,
}

InfiniteScrollGameGrid.propTypes = {
  games: PropTypes.array,
  reset: PropTypes.any,
  createGameTile: PropTypes.func,
  columns: PropTypes.number,
}

DefaultGameGrid.propTypes = {
  games: PropTypes.array,
  createGameTile: PropTypes.func,
  columns: PropTypes.number,
  rows: PropTypes.number,
}
